import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { apiErr } from "@/lib/api";
import { MapPin, Plus, Trash2, X as XIcon, Power } from "lucide-react";
import { toast } from "sonner";

const PRESET_COLORS = ["#2C4667", "#2A9FCB", "#9FD8EA", "#E07B39", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9", "#64748B"];
const MAX_SITES = 10;

function SiteForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState(initial || { name: "", address: "", city: "", cap: "", province: "", phone: "", notes: "", color: "#2C4667", active: true });
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="site-form">
      <h3 className="font-extrabold text-brand-navy">{initial ? "Modifica sede" : "Nuova sede"}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <input data-testid="site-name" placeholder="Nome sede *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
        <input data-testid="site-address" placeholder="Indirizzo" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0] col-span-2" />
        <input data-testid="site-city" placeholder="Città" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
        <input data-testid="site-cap" placeholder="CAP" value={f.cap} maxLength={5} onChange={(e) => setF({ ...f, cap: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0]" />
        <input data-testid="site-province" placeholder="Prov." value={f.province} maxLength={2} onChange={(e) => setF({ ...f, province: e.target.value.toUpperCase() })} className="px-3 py-2 rounded-lg border border-[#E2E8F0] uppercase" />
        <input data-testid="site-phone" placeholder="Telefono sede" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0] col-span-2" />
        <input data-testid="site-notes" placeholder="Note" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="px-3 py-2 rounded-lg border border-[#E2E8F0] col-span-3" />
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Colore / layout</div>
        <div className="flex flex-wrap gap-2" data-testid="site-color-palette">
          {PRESET_COLORS.map((c) => (
            <button key={c} type="button" data-testid={`site-color-${c.slice(1)}`} onClick={() => setF({ ...f, color: c })}
              className={`w-9 h-9 rounded-lg border-2 transition ${f.color === c ? "border-brand-navy ring-2 ring-brand-navy/30" : "border-white"}`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
          <input type="color" data-testid="site-color-custom" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="w-9 h-9 rounded-lg border-2 border-[#E2E8F0] cursor-pointer" />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Annulla</button>
        <button data-testid="site-save" onClick={() => onSave(f)} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg">{initial ? "Salva modifiche" : "Crea sede"}</button>
      </div>
    </div>
  );
}

export default function Sedi() {
  const [sites, setSites] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/medico/sites"); setSites(data); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (f) => {
    if (!f.name.trim()) { toast.error("Nome obbligatorio"); return; }
    if (sites.length >= MAX_SITES) { toast.error(`Massimo ${MAX_SITES} sedi per medico`); return; }
    try { await api.post("/medico/sites", f); toast.success("Sede creata"); setCreating(false); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const update = async (f) => {
    try { await api.patch(`/medico/sites/${editing.id}`, f); toast.success("Modifiche salvate"); setEditing(null); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const toggleActive = async (s) => {
    try { await api.patch(`/medico/sites/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };
  const del = async (s) => {
    if (!window.confirm(`Eliminare la sede "${s.name}"?`)) return;
    try { await api.delete(`/medico/sites/${s.id}`); toast.success("Sede eliminata"); load(); }
    catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const remaining = MAX_SITES - sites.length;

  return (
    <AppShell
      title="Sedi / Studi"
      subtitle={`Fino a ${MAX_SITES} location per il medico. ${remaining > 0 ? `Disponibili: ${remaining}` : "Limite raggiunto"}.`}
      rightSlot={!creating && !editing && sites.length < MAX_SITES && (
        <button data-testid="open-create-site" onClick={() => setCreating(true)} className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-4 py-2.5 rounded-lg flex items-center gap-2"><Plus size={16} /> Nuova sede</button>
      )}
    >
      {creating && <div className="mb-4"><SiteForm onCancel={() => setCreating(false)} onSave={create} /></div>}
      {editing && <div className="mb-4"><SiteForm initial={editing} onCancel={() => setEditing(null)} onSave={update} /></div>}

      {loading ? <div className="text-slate-500 py-8 text-center">Caricamento…</div> : sites.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center">
          <MapPin className="mx-auto text-slate-300 mb-3" size={36} />
          <div className="font-bold text-brand-navy">Nessuna sede</div>
          <div className="text-sm text-slate-500">Aggiungi la prima sede per associarla agli appuntamenti.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sites-list">
          {sites.map((s) => (
            <div key={s.id} data-testid={`site-card-${s.id}`} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
              <div className="h-2" style={{ backgroundColor: s.color || "#2C4667" }}></div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-extrabold text-brand-navy text-lg flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color || "#2C4667" }}></span>
                      {s.name}
                      {!s.active && <span className="text-[10px] uppercase font-bold bg-slate-200 text-slate-500 px-2 py-0.5 rounded">disattivata</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{s.address || "—"}</div>
                    <div className="text-xs text-slate-500">{[s.city, s.cap, s.province].filter(Boolean).join(" · ") || "—"}</div>
                    {s.phone && <div className="text-xs text-slate-500 mt-1">Tel: {s.phone}</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`site-toggle-${s.id}`} onClick={() => toggleActive(s)} title={s.active ? "Disattiva" : "Attiva"} className="p-1.5 rounded hover:bg-slate-100"><Power size={14} className={s.active ? "text-emerald-600" : "text-slate-400"} /></button>
                    <button data-testid={`site-edit-${s.id}`} onClick={() => setEditing(s)} className="text-xs font-bold text-brand-azure hover:bg-brand-azure/10 px-2 py-1 rounded">Modifica</button>
                    <button data-testid={`site-delete-${s.id}`} onClick={() => del(s)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
